import { lazy } from 'react';
import { Code } from '@/ui/primitives';
import { DescriptionOutlinedIcon, FolderOutlinedIcon } from '@/ui/primitives/icons';
import type { AuthSession } from '@/auth/types';

const CoverageRunsPage = lazy(async () => {
  const module = await import('@/features/codeCoverage/routes');
  return { default: module.CoverageRunsPage };
});

const CoverageDirectoriesPage = lazy(async () => {
  const module = await import('@/features/codeCoverage/routes');
  return { default: module.CoverageDirectoriesPage };
});

const CoverageFilesIndexPage = lazy(async () => {
  const module = await import('@/features/codeCoverage/routes');
  return { default: module.CoverageFilesIndexPage };
});

const CoverageRunDetailPage = lazy(async () => {
  const module = await import('@/features/codeCoverage/routes');
  return { default: module.CoverageRunDetailPage };
});

const CoverageDirectoryDetailPage = lazy(async () => {
  const module = await import('@/features/codeCoverage/routes');
  return { default: module.CoverageDirectoryDetailPage };
});

const CoverageFileDetailPage = lazy(async () => {
  const module = await import('@/features/codeCoverage/routes');
  return { default: module.CoverageFileDetailPage };
});

const projectScopedRoutePath = (path: string): string =>
  `/workspaces/:workspaceSlug/projects/:projectSlug${path}`;

export const codeCoverageRouteDefinitions = [
  {
    key: 'coverage-runs',
    path: '/coverage-runs',
    title: 'Coverage Runs',
    description:
      'Track uploaded code coverage runs and verify that CI coverage ingestion is working.',
    requiresAuth: true,
    element: () => <CoverageRunsPage />,
    nav: {
      label: 'Coverage Runs',
      icon: <Code fontSize="small" />,
    },
  },
  {
    key: 'workspace-project-coverage-runs',
    path: projectScopedRoutePath('/coverage-runs'),
    title: 'Coverage Runs',
    description:
      'Track uploaded code coverage runs and verify that CI coverage ingestion is working.',
    requiresAuth: true,
    element: () => <CoverageRunsPage />,
  },
  {
    key: 'coverage-directories',
    path: '/coverage-directories',
    title: 'Directories',
    requiresAuth: true,
    element: () => <CoverageDirectoriesPage />,
    nav: {
      label: 'Directories',
      icon: <FolderOutlinedIcon fontSize="small" />,
    },
  },
  {
    key: 'workspace-project-coverage-directories',
    path: projectScopedRoutePath('/coverage-directories'),
    title: 'Directories',
    requiresAuth: true,
    element: () => <CoverageDirectoriesPage />,
  },
  {
    key: 'coverage-files',
    path: '/coverage-files',
    title: 'Files',
    requiresAuth: true,
    element: () => <CoverageFilesIndexPage />,
    nav: {
      label: 'Files',
      icon: <DescriptionOutlinedIcon fontSize="small" />,
    },
  },
  {
    key: 'workspace-project-coverage-files',
    path: projectScopedRoutePath('/coverage-files'),
    title: 'Files',
    requiresAuth: true,
    element: () => <CoverageFilesIndexPage />,
  },
  {
    key: 'coverage-run-detail',
    path: '/coverage-runs/:id',
    title: 'Coverage Runs',
    requiresAuth: true,
    element: ({ session }: { session: AuthSession | null }) => (
      <CoverageRunDetailPage session={session} />
    ),
  },
  {
    key: 'workspace-project-coverage-run-detail',
    path: projectScopedRoutePath('/coverage-runs/:id'),
    title: 'Coverage Runs',
    requiresAuth: true,
    element: ({ session }: { session: AuthSession | null }) => (
      <CoverageRunDetailPage session={session} />
    ),
  },
  {
    key: 'coverage-directory-detail',
    path: '/coverage-directories/:id',
    title: 'Directories',
    requiresAuth: true,
    element: ({ session }: { session: AuthSession | null }) => (
      <CoverageDirectoryDetailPage session={session} />
    ),
  },
  {
    key: 'workspace-project-coverage-directory-detail',
    path: projectScopedRoutePath('/coverage-directories/:id'),
    title: 'Directories',
    requiresAuth: true,
    element: ({ session }: { session: AuthSession | null }) => (
      <CoverageDirectoryDetailPage session={session} />
    ),
  },
  {
    key: 'coverage-file-detail',
    path: '/coverage-files/:id',
    title: 'Files',
    requiresAuth: true,
    element: ({ session }: { session: AuthSession | null }) => (
      <CoverageFileDetailPage session={session} />
    ),
  },
  {
    key: 'workspace-project-coverage-file-detail',
    path: projectScopedRoutePath('/coverage-files/:id'),
    title: 'Files',
    requiresAuth: true,
    element: ({ session }: { session: AuthSession | null }) => (
      <CoverageFileDetailPage session={session} />
    ),
  },
] as const;
